import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  Icon,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  TextInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, PlusCircleIcon, SearchIcon, UsersIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useApiFetch } from '../../api';
import { useNotifications } from '../../contexts/NotificationContext';
import type { ApiResponseUserList } from '../../types/api/ApiResponseUser';
import type { User } from '../../types/models';

interface InviteFormState {
  firstname: string;
  lastname: string;
  email: string;
}

interface InviteFormErrors {
  firstname?: string;
  lastname?: string;
  email?: string;
}

const EMPTY_INVITE_FORM: InviteFormState = { firstname: '', lastname: '', email: '' };

function Team() {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteFormState>(EMPTY_INVITE_FORM);
  const [inviteErrors, setInviteErrors] = useState<InviteFormErrors>({});
  const [isInviting, setIsInviting] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch('/users');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: ApiResponseUserList = await response.json();
      setUsers(data.data.users);
    } catch (err) {
      console.error('Error fetching team:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    void fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  const filteredUsers = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.firstname ?? ''} ${u.lastname ?? ''}`.toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [users, debouncedSearch]);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredUsers.slice(start, start + perPage);
  }, [filteredUsers, page, perPage]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const validateInvite = (form: InviteFormState): InviteFormErrors => {
    const errs: InviteFormErrors = {};
    if (!form.firstname.trim()) errs.firstname = 'First name is required';
    if (!form.lastname.trim()) errs.lastname = 'Last name is required';
    if (!form.email.trim()) {
      errs.email = 'Email is required';
    }
    return errs;
  };

  const resetInviteForm = () => {
    setInviteForm(EMPTY_INVITE_FORM);
    setInviteErrors({});
  };

  const handleInviteConfirm = async () => {
    const errs = validateInvite(inviteForm);
    setInviteErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsInviting(true);
    try {
      const response = await apiFetch('/users/invite', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: JSON.stringify({
          firstname: inviteForm.firstname.trim(),
          lastname: inviteForm.lastname.trim(),
          email: inviteForm.email.trim(),
        }),
      });

      if (!response.ok) {
        let message = 'Failed to send invitation';
        try {
          const body = (await response.json()) as { message?: string };
          if (body?.message) message = body.message;
        } catch {
          // ignore
        }
        throw new Error(message);
      }

      addNotification({
        title: 'Invitation sent',
        srTitle: 'Success alert',
        description: `An invitation email has been sent to ${inviteForm.email.trim()}`,
        variant: 'success',
      });
      setDrawerOpen(true);
      setIsInviteOpen(false);
      resetInviteForm();
      void fetchTeam();
    } catch (err) {
      addNotification({
        title: 'Invitation failed',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'Could not send the invitation',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsInviting(false);
    }
  };

  const handleModalClose = () => {
    setIsInviteOpen(false);
    resetInviteForm();
  };

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load team"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void fetchTeam()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (users.length === 0) {
      return (
        <EmptyState titleText="No team members yet" icon={UsersIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>Invite your first team member to collaborate on your organization.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsInviteOpen(true)}>
                Invite user
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by name or email..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filteredUsers.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filteredUsers.length === 0 ? (
          <EmptyState titleText="No team members match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Try a different search term, or clear the filter to see all team members.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => setSearchValue('')}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Organization team" variant="compact" borders={false}>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>User ID</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedUsers.map((u) => {
                const hasName = (u.firstname ?? '').trim() !== '' || (u.lastname ?? '').trim() !== '';
                return (
                  <Tr key={u.sub ?? `${u.id}`}>
                    <Td dataLabel="Name">
                      {hasName ? (
                        <>
                          {u.firstname ?? ''} {u.lastname ?? ''}
                        </>
                      ) : (
                        <Label color="grey">Unknown</Label>
                      )}
                    </Td>
                    <Td dataLabel="Email">{u.email ?? '—'}</Td>
                    <Td dataLabel="User ID">{u.sub ?? '—'}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}

        {filteredUsers.length > 0 && (
          <Pagination
            itemCount={filteredUsers.length}
            perPage={perPage}
            page={page}
            onSetPage={onSetPage}
            onPerPageSelect={onPerPageSelect}
            variant="bottom"
          />
        )}
      </>
    );
  };

  const showInviteButton = !loading;

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/organization" isActive>
            Organization
          </BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <UsersIcon />
              </Icon>
              Team
            </Content>
            <Content component="p">Invite and manage additional users for your organization.</Content>
          </FlexItem>
          {showInviteButton && (
            <FlexItem>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsInviteOpen(true)}>
                Invite user
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>

      <Modal
        variant={ModalVariant.medium}
        isOpen={isInviteOpen}
        onClose={handleModalClose}
        aria-labelledby="invite-modal-title"
        aria-describedby="invite-modal-description"
      >
        <ModalHeader
          title="Invite a user"
          description="Enter the details of the user you want to invite to your team."
          labelId="invite-modal-title"
          descriptorId="invite-modal-description"
        />
        <ModalBody>
          <Form id="invite-user-form" isWidthLimited>
            <FormGroup label="First name" isRequired fieldId="invite-firstname">
              <TextInput
                id="invite-firstname"
                name="firstname"
                value={inviteForm.firstname}
                onChange={(_e, val) => setInviteForm((prev) => ({ ...prev, firstname: val }))}
                isRequired
                validated={inviteErrors.firstname ? 'error' : 'default'}
              />
            </FormGroup>
            <FormGroup label="Last name" isRequired fieldId="invite-lastname">
              <TextInput
                id="invite-lastname"
                name="lastname"
                value={inviteForm.lastname}
                onChange={(_e, val) => setInviteForm((prev) => ({ ...prev, lastname: val }))}
                isRequired
                validated={inviteErrors.lastname ? 'error' : 'default'}
              />
            </FormGroup>
            <FormGroup label="Email" isRequired fieldId="invite-email">
              <TextInput
                id="invite-email"
                name="email"
                type="email"
                value={inviteForm.email}
                onChange={(_e, val) => setInviteForm((prev) => ({ ...prev, email: val }))}
                isRequired
                validated={inviteErrors.email ? 'error' : 'default'}
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="primary"
            form="invite-user-form"
            isLoading={isInviting}
            isDisabled={isInviting}
            onClick={() => void handleInviteConfirm()}
          >
            Confirm
          </Button>
          <Button variant="link" onClick={handleModalClose} isDisabled={isInviting}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default Team;
